import { defaultLocale, locales } from "shared/constants/locales"

import I18n from "i18n"

export function baseLanguage(tag: string): string {
  return tag.split("-")[0].toLowerCase()
}

function browserPreferences(): string[] {
  if (typeof navigator === "undefined") {
    return []
  }
  if (navigator.languages?.length) {
    return [...navigator.languages]
  }
  return navigator.language ? [navigator.language] : []
}

// Deliberately stateless: a previously chosen locale is never persisted, so
// every first visit negotiates afresh — the same contract the backend has.
export function resolvePreferredLocale(): string {
  return (
    browserPreferences()
      .map(baseLanguage)
      .find(language => locales.includes(language)) ?? defaultLocale
  )
}

export function localeFromPathname(): string | undefined {
  if (typeof window === "undefined") {
    return undefined
  }

  const fromUrl = baseLanguage(window.location.pathname.split("/")[1] ?? "")
  return locales.includes(fromUrl) ? fromUrl : undefined
}

// The language the client must start at, before the first render: a prerendered
// page was rendered in its own locale, so booting i18next at the default locale
// makes React discard the SSR markup and repaint in the wrong language.
export function initialLocale(): string {
  return localeFromPathname() ?? resolvePreferredLocale()
}

// The single canonical read for the content language. The URL locale segment is
// read directly because i18next lags it by one effect on first render —
// LocaleVerifier only pushes the URL locale in via useEffect → changeLanguage.
// i18next is kept as a fallback for prerendered / SSR contexts where window is
// absent (in Node, i18next is already set correctly by the prerender bootstrap).
export function getContentLanguage(): string {
  const fromUrl = localeFromPathname()
  if (fromUrl) {
    return fromUrl
  }

  const language = baseLanguage(I18n.language ?? "")
  return locales.includes(language) ? language : defaultLocale
}
